'use client';

import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { CheckCircle2, Clock, X, Camera, Check } from 'lucide-react';
import type { ChildEntryType } from '../types';
import { ENTRY_CONFIG } from './expense/constants';
import {
  getSmartTaskViewportPageFrameClassName,
  getSmartTaskViewportPageFrameStyle,
  useSmartTaskViewport,
} from '../viewport';

const SUBMISSION_RECEIPT_Z_INDEX = 210;

export interface SubmissionReceiptData {
  entryType: ChildEntryType;
  intakeId?: string | number;
  amount?: number;
  currency?: string;
  vendor?: string;
  expenseDate?: string;
  photoCount: number;
  receiptAiValidated: boolean;
  requiresApproval: boolean;
  approvalLevel?: number;
  workflowMode?: string;
  itemNumber?: string;
}

export interface SubmissionReceiptProps {
  data: SubmissionReceiptData;
  onDismiss: () => void;
  autoDismissMs?: number;
  /** When provided, shows "Capture another" and "Done" buttons instead of auto-dismissing.
   *  onCaptureAnother resets the form for a new capture with the same context.
   *  onDone propagates the submission to the parent and closes the form. */
  onCaptureAnother?: () => void;
  onDone?: () => void;
}

interface StatusInfo {
  label: string;
  description: string;
  icon: React.ElementType;
}

function getStatusInfo(data: SubmissionReceiptData): StatusInfo {
  if (data.requiresApproval) {
    const level = data.approvalLevel || 1;
    return {
      label: `Pending Level ${level} Approval`,
      description: 'This expense has been queued for approval review.',
      icon: Clock,
    };
  }

  if (data.workflowMode === 'strict') {
    return {
      label: 'Pending Operations Review',
      description: 'This document has been submitted for operations review before finance approval.',
      icon: Clock,
    };
  }

  return {
    label: 'Submitted for Processing',
    description: 'This expense has been accepted and is being processed.',
    icon: CheckCircle2,
  };
}

function isCapacitorApp(): boolean {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

export function SubmissionReceipt({ data, onDismiss, autoDismissMs = 4000, onCaptureAnother, onDone }: SubmissionReceiptProps) {
  const [visible, setVisible] = useState(false);
  const { isTabletPortal } = useSmartTaskViewport();
  const isMobile = isCapacitorApp();
  // Show action buttons when EITHER onCaptureAnother or onDone is provided.
  // This ensures single-photo flows (fuel/toll with only onDone) still show
  // a "Done" button instead of auto-dismissing and leaving the user on the
  // form with stale data.
  const hasActionButtons = !!onCaptureAnother || !!onDone;
  const showCaptureAnother = !!onCaptureAnother && !!onDone;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    // Only auto-dismiss when action buttons are not shown
    if (autoDismissMs > 0 && !hasActionButtons) {
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onDismiss, 200);
      }, autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [autoDismissMs, onDismiss, hasActionButtons]);

  const dismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 200);
  };

  const status = getStatusInfo(data);
  const StatusIcon = status.icon;
  const config = ENTRY_CONFIG[data.entryType];
  const entryLabel = config?.label || data.entryType;

  return (
    <div
      className={`fixed flex flex-col bg-white dark:bg-gray-900 transition-opacity duration-200 ${getSmartTaskViewportPageFrameClassName({
        isTabletPortal,
        isMobile,
      })} ${visible ? 'opacity-100' : 'opacity-0'}`}
      style={{
        ...getSmartTaskViewportPageFrameStyle({
          isTabletPortal,
          isMobile,
          headerHidden: false,
        }),
        zIndex: SUBMISSION_RECEIPT_Z_INDEX,
      }}
      onClick={() => {
        if (hasActionButtons) return; // Don't dismiss on overlay click when action buttons are shown
        dismiss();
      }}
    >
      {/* ── Sticky header — matches EntryFormHeader / SmartTaskModalWrapper ── */}
      <div className="shadow-sm flex-shrink-0 bg-white dark:bg-gray-800 transition-[padding] duration-200">
        <div className="max-w-4xl mx-auto px-3 py-3 relative">
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Centered icon + bold title */}
          <div className="text-center pt-1">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14">
              <StatusIcon className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">{status.label}</h1>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{entryLabel}</p>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-sm flex-col items-center justify-center px-5 py-8">
          {/* DIN / reference — the key identifier for this submission */}
          {data.itemNumber && (
            <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-6 py-3 text-center dark:border-gray-700 dark:bg-gray-800/60">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Reference</div>
              <div className="mt-0.5 font-mono text-xl font-bold text-gray-900 dark:text-white">{data.itemNumber}</div>
            </div>
          )}

          {/* Evidence summary */}
          <div className="mt-4 w-full space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
            {data.amount != null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Amount</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {data.currency || 'ZAR'} {data.amount.toFixed(2)}
                </span>
              </div>
            )}
            {data.vendor && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Vendor</span>
                <span className="font-medium text-gray-900 dark:text-white truncate ml-2 max-w-[160px]">{data.vendor}</span>
              </div>
            )}
            {data.expenseDate && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Date</span>
                <span className="font-medium text-gray-900 dark:text-white">{data.expenseDate}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">Photos</span>
              <span className="font-medium text-gray-900 dark:text-white">{data.photoCount}</span>
            </div>
            {data.receiptAiValidated && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">AI Receipt Check</span>
                <span className="font-medium text-green-600 dark:text-green-400">Validated</span>
              </div>
            )}
          </div>

          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">{status.description}</p>
        </div>
      </div>

      {/* ── Bottom action bar (Done / Capture another) ──────────── */}
      {hasActionButtons ? (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 pb-3 pt-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="mx-auto flex max-w-4xl gap-2">
            {showCaptureAnother && (
              <button
                type="button"
                onClick={() => {
                  setVisible(false);
                  setTimeout(onCaptureAnother!, 200);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Camera className="h-4 w-4" />
                Capture another
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setVisible(false);
                setTimeout(onDone!, 200);
              }}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-white transition-all active:scale-95"
              style={{ backgroundColor: '#059669' }}
            >
              <Check className="h-4 w-4" />
              Done
            </button>
          </div>
        </div>
      ) : (
        /* Dismiss hint (auto-dismiss mode) */
        autoDismissMs > 0 && (
          <div className="flex-shrink-0 pb-6 text-center">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">Tap anywhere to dismiss</span>
          </div>
        )
      )}
    </div>
  );
}
